import React, { useState, useEffect, useCallback, useRef } from 'react';
import { gdService, gatePassService, invoiceService, batchService, containerAgingService } from '../../services/financeService';
import { buildGDRows } from '../../utils/exportUtils';
import AttachmentPanel from './AttachmentPanel';
import SearchableSelect from './SearchableSelect';
import CsvActions from '../common/CsvActions';
import './GDManagement.css';

const formatEnum = (v) => v ? v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—';
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const formatUSD = (v) => v != null ? `USD ${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
const formatPKR = (v) => v != null ? `PKR ${Number(v).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

const GD_STATUSES = ['FILED', 'DOCUMENTS_SUBMITTED', 'SETTLEMENT_PENDING', 'SETTLED', 'CANCELLED'];
const STATUS_COLORS = {
  FILED: { bg: 'var(--color-info-light)', color: 'var(--color-primary)' },
  DOCUMENTS_SUBMITTED: { bg: 'var(--color-warning-light)', color: 'var(--color-warning)' },
  SETTLEMENT_PENDING: { bg: 'var(--color-warning-light)', color: 'var(--color-warning)' },
  SETTLED: { bg: 'var(--color-success-light)', color: 'var(--color-success)' },
  CANCELLED: { bg: 'var(--color-neutral-100)', color: 'var(--color-text-secondary)' },
};
const DOC_TYPES = [
  'BILL_OF_LADING', 'PACKING_LIST', 'COMMERCIAL_INVOICE', 'LC', 'FORM_E',
  'BANK_ADVICE', 'BANK_STATEMENT', 'CERTIFICATE_OF_ORIGIN', 'INSPECTION_CERTIFICATE', 'OTHER',
];

const StatusBadge = ({ value }) => {
  const s = STATUS_COLORS[value] || { bg: 'var(--color-neutral-100)', color: 'var(--color-text-secondary)' };
  return (
    <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color }}>
      {formatEnum(value)}
    </span>
  );
};

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'register', label: 'GD Register' },
  { id: 'settlement', label: 'Dollar Settlement' },
  { id: 'aging', label: 'Payment Aging' },
];

const URGENCY_STYLE = {
  OVERDUE:  { bg: 'var(--color-error-light)', color: 'var(--color-error)', label: 'Overdue' },
  DUE_SOON: { bg: 'var(--color-warning-light)', color: 'var(--color-warning)', label: 'Due in 7 days' },
  UPCOMING: { bg: 'var(--color-warning-light)', color: 'var(--color-warning)', label: 'Due in 15 days' },
  SAFE:     { bg: 'var(--color-neutral-100)', color: 'var(--color-text-secondary)', label: 'Safe' },
  PAID:     { bg: 'var(--color-success-light)', color: 'var(--color-success)', label: 'Paid' },
  UNKNOWN:  { bg: 'var(--color-neutral-100)', color: 'var(--color-text-secondary)', label: '—' },
};

const emptyForm = () => ({
  gdNumber: '', consigneeName: '', consigneeCountry: '',
  shipmentDescription: '', portOfLoading: 'Karachi', portOfDestination: '',
  vesselOrFlight: '', billOfLadingNumber: '', invoiceNumber: '',
  invoiceValue: '', exportCurrency: 'USD', exchangeRate: '', bankCharges: '',
  netPkrReceived: '', filingDate: new Date().toISOString().split('T')[0],
  documentsSubmittedDate: '', settlementWindowDays: '30', settlementDate: '',
  bankName: '', bankAccountNumber: '', status: 'FILED', notes: '',
  linkedInvoiceId: '', linkedGatePassId: '', batchId: '',
});

const emptyDocForm = () => ({
  documentType: 'BILL_OF_LADING', documentName: '', description: '',
  documentDate: '', referenceNumber: '', notes: '',
});

export default function GDManagement() {
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [gdList, setGdList] = useState([]);
  const [settlementList, setSettlementList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Modals
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingGD, setEditingGD] = useState(null);
  const [detailGD, setDetailGD] = useState(null);
  const [statusGD, setStatusGD] = useState(null);
  const [deleteGD, setDeleteGD] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [docForm, setDocForm] = useState(emptyDocForm());
  const [docLoading, setDocLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [gatePasses, setGatePasses] = useState([]);
  const [batchList, setBatchList] = useState([]);

  // Payment Aging
  const [agingList, setAgingList] = useState([]);
  const [agingSummary, setAgingSummary] = useState(null);
  const [agingLoading, setAgingLoading] = useState(false);
  const [agingUploading, setAgingUploading] = useState(false);
  const [agingUploadMsg, setAgingUploadMsg] = useState(null);
  const [deleteAgingId, setDeleteAgingId] = useState(null);
  const [agingSearch, setAgingSearch] = useState('');
  const [agingFilter, setAgingFilter] = useState('ACTIONABLE'); // ACTIONABLE = everything except PAID
  const [agingPage, setAgingPage] = useState(0);
  const AGING_PAGE_SIZE = 50;

  // Toast notification
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceDropdownOpen, setInvoiceDropdownOpen] = useState(false);
  const invoicePickerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (invoicePickerRef.current && !invoicePickerRef.current.contains(e.target)) {
        setInvoiceDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const res = await gdService.getSummary();
      if (res.success) setSummary(res.data);
    } catch (e) { setError('Failed to load summary'); }
  }, []);

  const loadLinkedOptions = useCallback(async () => {
    try {
      const [invRes, gpRes] = await Promise.all([
        invoiceService.getAll().catch(() => []),
        gatePassService.getAll().catch(() => ({ data: { content: [] } })),
      ]);
      setInvoices(Array.isArray(invRes) ? invRes : (invRes?.data?.content || invRes?.data || []));
      const gpData = gpRes?.data?.content || gpRes?.data || [];
      setGatePasses(Array.isArray(gpData) ? gpData : []);
      const bRes = await batchService.getAll().catch(() => ({ data: [] }));
      const bl = bRes?.data || [];
      setBatchList(bl.filter(b => b.status === 'OPEN' || b.status === 'PROCESSING'));
    } catch (e) { setError('Failed to load linked options'); }
  }, []);

  const loadGDs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size: 20 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await gdService.getAll(params);
      if (res.success) {
        setGdList(Array.isArray(res.data) ? res.data : []);
        setTotal(res.totalElements || 0);
      }
    } catch (e) { setError('Failed to load GDs'); }
    finally { setLoading(false); }
  }, [page, search, statusFilter]);

  const loadSettlement = useCallback(async () => {
    setLoading(true);
    try {
      const [submitted, pending] = await Promise.all([
        gdService.getAll({ status: 'DOCUMENTS_SUBMITTED', size: 50 }),
        gdService.getAll({ status: 'SETTLEMENT_PENDING', size: 50 }),
      ]);
      const combined = [
        ...(submitted.success ? (Array.isArray(submitted.data) ? submitted.data : []) : []),
        ...(pending.success ? (Array.isArray(pending.data) ? pending.data : []) : []),
      ].sort((a, b) => (a.settlementDeadline || '').localeCompare(b.settlementDeadline || ''));
      setSettlementList(combined);
    } catch (e) { setError('Failed to load settlement data'); }
    finally { setLoading(false); }
  }, []);

  const loadAging = useCallback(async () => {
    setAgingLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        containerAgingService.getAll(),
        containerAgingService.getSummary(),
      ]);
      setAgingList(listRes.success ? listRes.data : []);
      setAgingSummary(summaryRes.success ? summaryRes.data : null);
    } catch (e) { setError('Failed to load payment aging data'); }
    finally { setAgingLoading(false); }
  }, []);

  const handleAgingUpload = async (file) => {
    if (!file) return;
    setAgingUploading(true);
    setAgingUploadMsg(null);
    try {
      const res = await containerAgingService.upload(file);
      setAgingUploadMsg({ type: 'success', text: res.message });
      loadAging();
    } catch (e) {
      setAgingUploadMsg({ type: 'error', text: e.message || 'Upload failed' });
    } finally {
      setAgingUploading(false);
    }
  };

  const handleDeleteAging = async (id) => {
    try {
      await containerAgingService.remove(id);
      setDeleteAgingId(null);
      loadAging();
    } catch (e) {
      showToast(e.message || 'Failed to delete record', 'error');
    }
  };

  const toggleAgingFilter = (urgency) => {
    setAgingFilter(prev => (prev === urgency ? 'ACTIONABLE' : urgency));
    setAgingPage(0);
  };

  const filteredAgingList = agingList.filter(rec => {
    if (agingFilter === 'ACTIONABLE' && rec.urgency === 'PAID') return false;
    if (agingFilter !== 'ACTIONABLE' && agingFilter !== 'ALL' && rec.urgency !== agingFilter) return false;
    if (agingSearch.trim()) {
      const q = agingSearch.trim().toLowerCase();
      const haystack = `${rec.containerNumber || ''} ${rec.gdNumber || ''} ${rec.consigneeName || ''} ${rec.shipperName || ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
  const agingTotalPages = Math.max(1, Math.ceil(filteredAgingList.length / AGING_PAGE_SIZE));
  const pagedAgingList = filteredAgingList.slice(agingPage * AGING_PAGE_SIZE, (agingPage + 1) * AGING_PAGE_SIZE);

  const AGING_HEADERS = ['Container No.', 'GD Number', 'GD Date', 'Consignee', 'Country', 'FOB Value', 'Payment', 'Aging (days)', 'Urgency'];
  const buildAgingRows = () => filteredAgingList.map(r => [
    r.containerNumber || '', r.gdNumber || '', r.gdDate || '', r.consigneeName || '', r.countryName || '',
    r.fobValue ?? '', r.paymentReceived ? 'Received' : 'Outstanding', r.agingDays ?? '', (URGENCY_STYLE[r.urgency] || {}).label || r.urgency,
  ]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { if (activeTab === 'register') loadGDs(); }, [activeTab, loadGDs]);
  useEffect(() => { if (activeTab === 'settlement') loadSettlement(); }, [activeTab, loadSettlement]);
  useEffect(() => { if (activeTab === 'aging') loadAging(); }, [activeTab, loadAging]);
  useEffect(() => { setAgingPage(0); }, [agingList]);

  const openCreate = () => { setEditingGD(null); setForm(emptyForm()); setFormError(''); setInvoiceSearch(''); setInvoiceDropdownOpen(false); loadLinkedOptions(); setShowFormModal(true); };
  const openEdit = (gd) => {
    setEditingGD(gd);
    setForm({
      gdNumber: gd.gdNumber || '', consigneeName: gd.consigneeName || '',
      consigneeCountry: gd.consigneeCountry || '', shipmentDescription: gd.shipmentDescription || '',
      portOfLoading: gd.portOfLoading || '', portOfDestination: gd.portOfDestination || '',
      vesselOrFlight: gd.vesselOrFlight || '', billOfLadingNumber: gd.billOfLadingNumber || '',
      invoiceNumber: gd.invoiceNumber || '', invoiceValue: String(gd.invoiceValue || ''),
      exportCurrency: gd.exportCurrency || 'USD', exchangeRate: gd.exchangeRate != null ? String(gd.exchangeRate) : '',
      bankCharges: gd.bankCharges != null ? String(gd.bankCharges) : '',
      netPkrReceived: gd.netPkrReceived != null ? String(gd.netPkrReceived) : '',
      filingDate: gd.filingDate || '', documentsSubmittedDate: gd.documentsSubmittedDate || '',
      settlementWindowDays: String(gd.settlementWindowDays || 30), settlementDate: gd.settlementDate || '',
      bankName: gd.bankName || '', bankAccountNumber: gd.bankAccountNumber || '',
      status: gd.status || 'FILED', notes: gd.notes || '',
      linkedInvoiceId: gd.linkedInvoiceId || '', linkedGatePassId: gd.linkedGatePassId || '',
    });
    setFormError('');
    setInvoiceSearch('');
    setInvoiceDropdownOpen(false);
    loadLinkedOptions();
    setShowFormModal(true);
  };

  const openDetail = async (gd) => {
    try {
      const res = await gdService.getById(gd.id);
      if (res.success) { setDetailGD(res.data); setShowDetailModal(true); }
    } catch (e) { setError('Failed to load GD details'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(''); setFormLoading(true);
    try {
      const payload = {
        ...form,
        invoiceValue: parseFloat(form.invoiceValue),
        exchangeRate: form.exchangeRate ? parseFloat(form.exchangeRate) : null,
        bankCharges: form.bankCharges ? parseFloat(form.bankCharges) : null,
        netPkrReceived: form.netPkrReceived ? parseFloat(form.netPkrReceived) : null,
        settlementWindowDays: parseInt(form.settlementWindowDays) || 30,
        documentsSubmittedDate: form.documentsSubmittedDate || null,
        settlementDate: form.settlementDate || null,
        linkedInvoiceId: form.linkedInvoiceId ? parseInt(form.linkedInvoiceId) : null,
        linkedGatePassId: form.linkedGatePassId ? parseInt(form.linkedGatePassId) : null,
        batchId: form.batchId ? parseInt(form.batchId) : null,
      };
      const res = editingGD
        ? await gdService.update(editingGD.id, payload)
        : await gdService.create(payload);
      if (res.success) {
        setShowFormModal(false);
        setEditingGD(null);
        loadGDs(); loadSummary();
        showToast(editingGD ? 'GD updated successfully' : 'GD created successfully');
      } else {
        setFormError(res.message || 'Failed to save GD');
      }
    } catch (e) { setFormError(e.message || 'An error occurred'); }
    finally { setFormLoading(false); }
  };

  const handleUpdateStatus = async () => {
    if (!statusGD || !newStatus) return;
    setStatusLoading(true);
    try {
      const res = await gdService.updateStatus(statusGD.id, newStatus);
      if (res.success) {
        setShowStatusModal(false); setStatusGD(null); setNewStatus('');
        loadGDs(); loadSummary();
        if (activeTab === 'settlement') loadSettlement();
        showToast('Status updated successfully');
      }
    } catch (e) { setError('Failed to update status'); showToast('Failed to update status', 'error'); }
    finally { setStatusLoading(false); }
  };

  const handleDelete = async () => {
    if (!deleteGD) return;
    setDeleteLoading(true);
    try {
      const res = await gdService.delete(deleteGD.id);
      if (res.success) {
        setShowDeleteModal(false); setDeleteGD(null);
        loadGDs(); loadSummary();
        showToast('GD deleted successfully');
      }
    } catch (e) { setError('Failed to delete GD'); showToast('Failed to delete GD', 'error'); }
    finally { setDeleteLoading(false); }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();
    setDocLoading(true);
    try {
      const res = await gdService.addDocument(detailGD.id, {
        ...docForm,
        documentDate: docForm.documentDate || null,
      });
      if (res.success) {
        setDocForm(emptyDocForm());
        setShowDocModal(false);
        const refreshed = await gdService.getById(detailGD.id);
        if (refreshed.success) setDetailGD(refreshed.data);
      }
    } catch (e) { setError('Failed to add document'); }
    finally { setDocLoading(false); }
  };

  const [removeDocId, setRemoveDocId] = useState(null);

  const handleRemoveDocument = async () => {
    if (!removeDocId) return;
    try {
      const res = await gdService.removeDocument(detailGD.id, removeDocId);
      if (res.success) {
        const refreshed = await gdService.getById(detailGD.id);
        if (refreshed.success) setDetailGD(refreshed.data);
      }
    } catch (e) { setError('Failed to remove document'); }
    finally { setRemoveDocId(null); }
  };

  const deadlineLabel = (days) => {
    if (days == null) return null;
    if (days < 0) return { text: `${Math.abs(days)}d OVERDUE`, bg: 'var(--color-error-light)', color: 'var(--color-error)' };
    if (days === 0) return { text: 'Due TODAY', bg: 'var(--color-warning-light)', color: 'var(--color-warning)' };
    if (days <= 7) return { text: `${days}d left`, bg: 'var(--color-warning-light)', color: 'var(--color-warning)' };
    return { text: `${days}d left`, bg: 'var(--color-success-light)', color: 'var(--color-success)' };
  };

  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleInvoiceSelect = (invoiceId) => {
    if (!invoiceId) {
      setForm(prev => ({ ...prev, linkedInvoiceId: '', invoiceNumber: '', invoiceValue: '' }));
      return;
    }
    const inv = invoices.find(i => String(i.id) === String(invoiceId));
    if (inv) {
      setForm(prev => ({
        ...prev,
        linkedInvoiceId: String(inv.id),
        invoiceNumber: inv.invoiceNumber || '',
        invoiceValue: inv.totalAmount != null ? String(inv.totalAmount) : '',
        consigneeName: prev.consigneeName || inv.customerName || '',
      }));
    }
  };

  return (
    <div className="gd-page">
      {/* Header */}
      <div className="gd-header">
        <div>
          <h1 className="gd-title">GD & Export Documents</h1>
          <p className="gd-subtitle">Goods Declarations, bank submissions & dollar settlement</p>
        </div>
        <div className="gd-header-actions">
          {activeTab === 'register' && (() => { const { headers, matrix } = buildGDRows(gdList); return (
            <CsvActions filename="gd_register" headers={headers} matrix={matrix} className="gd-btn gd-btn-secondary" />
          ); })()}
          {(activeTab === 'register' || activeTab === 'overview') && (
            <button className="gd-btn gd-btn-primary" onClick={openCreate}>+ New GD</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="gd-tabs">
        {TABS.map(t => (
          <button key={t.id} className={`gd-tab ${activeTab === t.id ? 'gd-tab-active' : ''}`}
            onClick={() => setActiveTab(t.id)}>
            {t.label}
            {t.id === 'overview' && summary?.upcomingDeadlineCount > 0 && (
              <span className="gd-badge-alert">{summary.upcomingDeadlineCount}</span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="gd-alert gd-alert-error">
          {error}
          <button onClick={() => setError('')} className="gd-alert-close">×</button>
        </div>
      )}

      {/* ===== OVERVIEW ===== */}
      {activeTab === 'overview' && (
        <div className="gd-content">
          <div className="gd-summary-grid">
            <div className="gd-card gd-card-blue">
              <div className="gd-card-label">Total GDs</div>
              <div className="gd-card-value">{summary?.totalGDs ?? 0}</div>
            </div>
            <div className="gd-card gd-card-orange">
              <div className="gd-card-label">Pending Settlement</div>
              <div className="gd-card-value">{summary?.pendingSettlements ?? 0}</div>
              <div className="gd-card-sub">awaiting USD receipt</div>
            </div>
            <div className="gd-card gd-card-green">
              <div className="gd-card-label">Total USD Value</div>
              <div className="gd-card-value">{formatUSD(summary?.totalUSDValue)}</div>
              <div className="gd-card-sub">active GDs</div>
            </div>
            <div className="gd-card gd-card-purple">
              <div className="gd-card-label">Settled This Month</div>
              <div className="gd-card-value">{formatPKR(summary?.settledPkrThisMonth)}</div>
            </div>
          </div>

          {summary?.upcomingDeadlineGDs?.length > 0 && (
            <div className="gd-alert-section">
              <div className="gd-alert-header">
                Settlement Deadlines — Action Required ({summary.upcomingDeadlineCount})
              </div>
              {summary.upcomingDeadlineGDs.map(gd => {
                const dl = deadlineLabel(gd.daysUntilDeadline);
                return (
                  <div key={gd.id} className="gd-alert-row">
                    <div className="gd-alert-left">
                      <span className="gd-mono">{gd.gdNumber}</span>
                      <span className="gd-alert-consignee">{gd.consigneeName}</span>
                      <StatusBadge value={gd.status} />
                    </div>
                    <div className="gd-alert-right">
                      <strong>{formatUSD(gd.invoiceValue)}</strong>
                      {dl && <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: dl.bg, color: dl.color }}>{dl.text}</span>}
                      <span className="gd-date-muted">Deadline: {formatDate(gd.settlementDeadline)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {(!summary?.upcomingDeadlineGDs?.length) && summary && (
            <div className="gd-empty">
              <div className="gd-empty-icon">—</div>
              <div className="gd-empty-title">No Upcoming Deadlines</div>
              <div className="gd-empty-sub">All settlements are on track within 7 days.</div>
            </div>
          )}
        </div>
      )}

      {/* ===== GD REGISTER ===== */}
      {activeTab === 'register' && (
        <div className="gd-content">
          <div className="gd-filters">
            <input className="gd-input gd-search" placeholder="Search by GD#, consignee, BL#, invoice#..."
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} />
            <select className="gd-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
              <option value="">All Statuses</option>
              {GD_STATUSES.map(s => <option key={s} value={s}>{formatEnum(s)}</option>)}
            </select>
          </div>

          {loading ? <div className="gd-loading">Loading...</div>
          : gdList.length === 0 ? (
            <div className="gd-empty">
              <div className="gd-empty-icon">—</div>
              <div className="gd-empty-title">No GDs Found</div>
              <div className="gd-empty-sub">Create your first Goods Declaration to get started.</div>
            </div>
          ) : (
            <div className="gd-table-wrap">
              <table className="gd-table">
                <thead>
                  <tr>
                    <th>GD Number</th>
                    <th>Consignee</th>
                    <th>Invoice Value</th>
                    <th>Filing Date</th>
                    <th>Settlement Deadline</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {gdList.map(gd => {
                    const dl = deadlineLabel(gd.daysUntilDeadline);
                    const isActive = !['SETTLED','CANCELLED'].includes(gd.status);
                    return (
                      <tr key={gd.id} className={dl && dl.color === 'var(--color-error)' && isActive ? 'gd-row-warn' : ''}>
                        <td><span className="gd-mono">{gd.gdNumber}</span></td>
                        <td>
                          <div className="gd-consignee-name">{gd.consigneeName}</div>
                          {gd.consigneeCountry && <div className="gd-consignee-country">{gd.consigneeCountry}</div>}
                        </td>
                        <td className="gd-amount">{formatUSD(gd.invoiceValue)}</td>
                        <td>{formatDate(gd.filingDate)}</td>
                        <td>
                          {gd.settlementDeadline ? (
                            <>
                              <div>{formatDate(gd.settlementDeadline)}</div>
                              {dl && isActive && (
                                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, fontWeight: 700, background: dl.bg, color: dl.color }}>{dl.text}</span>
                              )}
                            </>
                          ) : '—'}
                        </td>
                        <td><StatusBadge value={gd.status} /></td>
                        <td className="gd-actions">
                          <button className="gd-btn-link" onClick={() => openDetail(gd)}>View</button>
                          <button className="gd-btn-link gd-link-edit" onClick={() => openEdit(gd)}>Edit</button>
                          {isActive && (
                            <button className="gd-btn-link gd-link-muted"
                              onClick={() => { setStatusGD(gd); setNewStatus(gd.status); setShowStatusModal(true); }}>
                              Status
                            </button>
                          )}
                          <button className="gd-btn-link gd-link-danger" onClick={() => { setDeleteGD(gd); setShowDeleteModal(true); }}>Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {total > 20 && (
            <div className="gd-pagination">
              <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="gd-btn gd-btn-sm">Prev</button>
              <span>Page {page + 1} of {Math.ceil(total / 20)}</span>
              <button disabled={(page + 1) * 20 >= total} onClick={() => setPage(p => p + 1)} className="gd-btn gd-btn-sm">Next</button>
            </div>
          )}
        </div>
      )}

      {/* ===== DOLLAR SETTLEMENT ===== */}
      {activeTab === 'settlement' && (
        <div className="gd-content">
          <p className="gd-section-hint">GDs with pending bank submission or settlement. Sorted by nearest deadline.</p>
          {loading ? <div className="gd-loading">Loading...</div>
          : settlementList.length === 0 ? (
            <div className="gd-empty">
              <div className="gd-empty-icon">—</div>
              <div className="gd-empty-title">No Pending Settlements</div>
              <div className="gd-empty-sub">All GDs are either settled or not yet submitted.</div>
            </div>
          ) : (
            <div className="gd-table-wrap">
              <table className="gd-table">
                <thead>
                  <tr>
                    <th>GD Number</th>
                    <th>Consignee</th>
                    <th>Invoice (USD)</th>
                    <th>Bank</th>
                    <th>Docs Submitted</th>
                    <th>Window</th>
                    <th>Deadline</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {settlementList.map(gd => {
                    const dl = deadlineLabel(gd.daysUntilDeadline);
                    return (
                      <tr key={gd.id} className={dl && dl.color === 'var(--color-error)' ? 'gd-row-warn' : ''}>
                        <td><span className="gd-mono">{gd.gdNumber}</span></td>
                        <td>
                          <div className="gd-consignee-name">{gd.consigneeName}</div>
                          {gd.consigneeCountry && <div className="gd-consignee-country">{gd.consigneeCountry}</div>}
                        </td>
                        <td className="gd-amount">{formatUSD(gd.invoiceValue)}</td>
                        <td>{gd.bankName || '—'}</td>
                        <td>{formatDate(gd.documentsSubmittedDate)}</td>
                        <td>{gd.settlementWindowDays ? `${gd.settlementWindowDays} days` : '—'}</td>
                        <td>
                          <div>{formatDate(gd.settlementDeadline)}</div>
                          {dl && (
                            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, fontWeight: 700, background: dl.bg, color: dl.color }}>{dl.text}</span>
                          )}
                        </td>
                        <td><StatusBadge value={gd.status} /></td>
                        <td className="gd-actions">
                          <button className="gd-btn-link" onClick={() => openDetail(gd)}>View</button>
                          <button className="gd-btn-link gd-link-muted"
                            onClick={() => { setStatusGD(gd); setNewStatus(gd.status); setShowStatusModal(true); }}>
                            Update
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ===== PAYMENT AGING ===== */}
      {activeTab === 'aging' && (
        <div className="gd-content">
          <p className="gd-section-hint">
            Upload your export-tracking sheets (company payment log, PSW GD data export) — matched and updated by Container Number.
            Aging is calculated against the State Bank's 120-day export-proceeds deadline: <strong>today − (GD Date + 120 days)</strong>.
            Positive = overdue; negative = days remaining.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
            <label className="gd-btn gd-btn-primary" style={{ cursor: agingUploading ? 'not-allowed' : 'pointer', opacity: agingUploading ? 0.6 : 1 }}>
              {agingUploading ? 'Uploading…' : '+ Upload Excel Sheet'}
              <input
                type="file" accept=".xlsx,.xls" style={{ display: 'none' }} disabled={agingUploading}
                onChange={e => { const f = e.target.files[0]; e.target.value = ''; handleAgingUpload(f); }}
              />
            </label>
            {agingList.length > 0 && (
              <CsvActions filename="payment_aging" headers={AGING_HEADERS} matrix={buildAgingRows()} className="gd-btn gd-btn-secondary" />
            )}
            {agingUploadMsg && (
              <span style={{ fontSize: 13, color: agingUploadMsg.type === 'error' ? 'var(--color-error)' : 'var(--color-success)' }}>
                {agingUploadMsg.text}
              </span>
            )}
          </div>

          {agingSummary && (
            <div className="gd-summary-grid" style={{ marginBottom: 20 }}>
              {[
                { key: 'OVERDUE', label: 'Overdue', bg: 'var(--color-error-light)', color: 'var(--color-error)', value: agingSummary.overdue },
                { key: 'DUE_SOON', label: 'Due in 7 Days', bg: 'var(--color-warning-light)', color: 'var(--color-warning)', value: agingSummary.dueIn7Days },
                { key: 'UPCOMING', label: 'Due in 15 Days', bg: 'var(--color-warning-light)', color: 'var(--color-warning)', value: agingSummary.dueIn15Days },
                { key: 'SAFE', label: 'Safe', bg: 'var(--color-neutral-100)', color: 'var(--color-text-secondary)', value: agingSummary.safe },
                { key: 'PAID', label: 'Paid', bg: 'var(--color-success-light)', color: 'var(--color-success)', value: agingSummary.paid },
              ].map(tile => (
                <button
                  key={tile.key}
                  className="gd-card"
                  onClick={() => toggleAgingFilter(tile.key)}
                  style={{
                    background: tile.bg, cursor: 'pointer', border: agingFilter === tile.key ? `2px solid ${tile.color}` : '2px solid transparent',
                    textAlign: 'left', font: 'inherit',
                  }}
                  title={agingFilter === tile.key ? 'Click to clear filter' : `Show only ${tile.label}`}
                >
                  <div className="gd-card-label" style={{ color: tile.color }}>{tile.label}</div>
                  <div className="gd-card-value" style={{ color: tile.color }}>{tile.value}</div>
                </button>
              ))}
            </div>
          )}

          {agingList.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
              <input
                type="text" placeholder="Search container, GD number, or consignee…"
                value={agingSearch}
                onChange={e => { setAgingSearch(e.target.value); setAgingPage(0); }}
                style={{ flex: '1 1 280px', maxWidth: 360, padding: '8px 12px', border: '1px solid var(--color-border-strong)', borderRadius: 8, fontSize: 13 }}
              />
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                {filteredAgingList.length.toLocaleString()} of {agingList.length.toLocaleString()} shown
                {agingFilter !== 'ACTIONABLE' && (
                  <button className="gd-btn-link" style={{ marginLeft: 8 }} onClick={() => setAgingFilter('ACTIONABLE')}>clear filter</button>
                )}
              </span>
            </div>
          )}

          {agingLoading ? <div className="gd-loading">Loading...</div>
          : agingList.length === 0 ? (
            <div className="gd-empty">
              <div className="gd-empty-icon">—</div>
              <div className="gd-empty-title">No Records Yet</div>
              <div className="gd-empty-sub">Upload an export-tracking Excel sheet above to get started.</div>
            </div>
          ) : filteredAgingList.length === 0 ? (
            <div className="gd-empty">
              <div className="gd-empty-icon">—</div>
              <div className="gd-empty-title">No Matches</div>
              <div className="gd-empty-sub">Try a different search term or clear the filter.</div>
            </div>
          ) : (
            <>
              <div className="gd-table-wrap">
                <table className="gd-table">
                  <thead>
                    <tr>
                      <th>Container No.</th>
                      <th>GD Number</th>
                      <th>GD Date</th>
                      <th>Consignee</th>
                      <th>Country</th>
                      <th>FOB Value</th>
                      <th>Payment</th>
                      <th>Aging</th>
                      <th>Urgency</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedAgingList.map(rec => {
                      const u = URGENCY_STYLE[rec.urgency] || URGENCY_STYLE.UNKNOWN;
                      return (
                        <tr key={rec.id}>
                          <td style={{ fontWeight: 600, letterSpacing: '0.02em' }}>{rec.containerNumber}</td>
                          <td>{rec.gdNumber || '—'}</td>
                          <td>{formatDate(rec.gdDate)}</td>
                          <td>
                            <div className="gd-consignee-name">{rec.consigneeName || '—'}</div>
                          </td>
                          <td>{rec.countryName || '—'}</td>
                          <td className="gd-amount">{rec.fobValue != null ? Number(rec.fobValue).toLocaleString() : '—'}</td>
                          <td>{rec.paymentReceived ? 'Received' : 'Outstanding'}</td>
                          <td style={{ fontWeight: 600 }}>
                            {rec.agingDays != null ? (rec.agingDays > 0 ? `+${rec.agingDays}d` : `${rec.agingDays}d`) : '—'}
                          </td>
                          <td>
                            <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: u.bg, color: u.color }}>
                              {u.label}
                            </span>
                          </td>
                          <td className="gd-actions">
                            {deleteAgingId === rec.id ? (
                              <span style={{ display: 'flex', gap: 6 }}>
                                <button className="gd-btn-link" style={{ color: 'var(--color-error)' }} onClick={() => handleDeleteAging(rec.id)}>Confirm</button>
                                <button className="gd-btn-link gd-link-muted" onClick={() => setDeleteAgingId(null)}>Cancel</button>
                              </span>
                            ) : (
                              <button className="gd-btn-link gd-link-muted" onClick={() => setDeleteAgingId(rec.id)}>Remove</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {agingTotalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginTop: 14 }}>
                  <button className="gd-btn gd-btn-secondary" disabled={agingPage === 0} onClick={() => setAgingPage(p => Math.max(0, p - 1))}>
                    ← Prev
                  </button>
                  <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Page {agingPage + 1} of {agingTotalPages}</span>
                  <button className="gd-btn gd-btn-secondary" disabled={agingPage >= agingTotalPages - 1} onClick={() => setAgingPage(p => Math.min(agingTotalPages - 1, p + 1))}>
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ===== CREATE / EDIT GD MODAL ===== */}
      {showFormModal && (
        <div className="gd-overlay" onClick={() => { setShowFormModal(false); setEditingGD(null); }}>
          <div className="gd-modal" onClick={e => e.stopPropagation()}>
            <div className="gd-modal-header">
              <h2>{editingGD ? `Edit GD — ${editingGD.gdNumber}` : 'New Goods Declaration'}</h2>
              <button className="gd-modal-close" onClick={() => { setShowFormModal(false); setEditingGD(null); }}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="gd-modal-body">
              {formError && <div className="gd-alert gd-alert-error">{formError}</div>}

              <div className="gd-section-label">Shipment Info</div>
              <div className="gd-grid-2">
                <div className="gd-field">
                  <label>GD Number * <span className="gd-hint">(from WEBOC/Customs)</span></label>
                  <input className="gd-input gd-full" placeholder="e.g. KPEX-HC-2026-1234567"
                    value={form.gdNumber} onChange={f('gdNumber')} required />
                </div>
                <div className="gd-field">
                  <label>Filing Date *</label>
                  <input type="date" className="gd-input gd-full" value={form.filingDate} onChange={f('filingDate')} required />
                </div>
                {!editingGD && batchList.length > 0 && (
                  <div className="gd-field">
                    <label>Link to Batch <span className="gd-hint">(optional)</span></label>
                    <SearchableSelect
                      placeholder="— No batch —"
                      value={form.batchId}
                      options={batchList.map(b => ({
                        value: b.id,
                        label: `${b.batchCode} · ${b.productName}${b.godownName ? ` · ${b.godownName}` : ''}`,
                      }))}
                      onChange={(value) => f('batchId')({ target: { value: String(value) } })}
                    />
                  </div>
                )}
                <div className="gd-field">
                  <label>Consignee Name *</label>
                  <input className="gd-input gd-full" placeholder="Buyer/receiver name"
                    value={form.consigneeName} onChange={f('consigneeName')} required />
                </div>
                <div className="gd-field">
                  <label>Consignee Country</label>
                  <input className="gd-input gd-full" placeholder="e.g. UK, UAE, China"
                    value={form.consigneeCountry} onChange={f('consigneeCountry')} />
                </div>
                <div className="gd-field">
                  <label>Port of Loading</label>
                  <input className="gd-input gd-full" placeholder="e.g. Karachi"
                    value={form.portOfLoading} onChange={f('portOfLoading')} />
                </div>
                <div className="gd-field">
                  <label>Port of Destination</label>
                  <input className="gd-input gd-full" placeholder="Destination port"
                    value={form.portOfDestination} onChange={f('portOfDestination')} />
                </div>
                <div className="gd-field">
                  <label>Vessel / Flight</label>
                  <input className="gd-input gd-full" placeholder="Vessel or flight name"
                    value={form.vesselOrFlight} onChange={f('vesselOrFlight')} />
                </div>
                <div className="gd-field">
                  <label>Bill of Lading #</label>
                  <input className="gd-input gd-full" placeholder="BL number"
                    value={form.billOfLadingNumber} onChange={f('billOfLadingNumber')} />
                </div>
                <div className="gd-field">
                  <label>Invoice *</label>
                  <div style={{ position: 'relative' }} ref={invoicePickerRef}>
                    {!form.linkedInvoiceId ? (
                      <>
                        <input
                          className="gd-input gd-full"
                          placeholder="Search by invoice #, customer, or amount..."
                          value={invoiceSearch}
                          onChange={e => { setInvoiceSearch(e.target.value); setInvoiceDropdownOpen(true); }}
                          onFocus={() => setInvoiceDropdownOpen(true)}
                          autoComplete="off"
                        />
                        {invoiceDropdownOpen && (() => {
                          const q = invoiceSearch.toLowerCase();
                          const filtered = q
                            ? invoices.filter(inv =>
                                (inv.invoiceNumber || '').toLowerCase().includes(q) ||
                                (inv.customerName || '').toLowerCase().includes(q) ||
                                String(inv.totalAmount || '').includes(q)
                              )
                            : invoices;
                          return filtered.length > 0 ? (
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: 'var(--color-bg-primary)', border: '1px solid var(--color-border-strong)', borderRadius: '0 0 8px 8px', maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                              {filtered.slice(0, 20).map(inv => (
                                <div key={inv.id}
                                  onClick={() => { handleInvoiceSelect(inv.id); setInvoiceSearch(''); setInvoiceDropdownOpen(false); }}
                                  style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--color-neutral-100)', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                  onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                                  onMouseLeave={e => e.currentTarget.style.background = 'var(--color-bg-primary)'}
                                >
                                  <div>
                                    <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{inv.invoiceNumber}</span>
                                    <span style={{ color: 'var(--color-text-secondary)', marginLeft: 8 }}>{inv.customerName || 'N/A'}</span>
                                    <span style={{ color: 'var(--color-text-tertiary)', marginLeft: 6, fontSize: 11 }}>{inv.invoiceDate || ''}</span>
                                  </div>
                                  <div style={{ fontWeight: 600, color: 'var(--color-primary)', whiteSpace: 'nowrap' }}>
                                    {inv.currency || 'PKR'} {Number(inv.totalAmount || 0).toLocaleString()}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : invoiceSearch ? (
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: 'var(--color-bg-primary)', border: '1px solid var(--color-border-strong)', borderRadius: '0 0 8px 8px', padding: '10px 12px', fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                              No invoices match "{invoiceSearch}"
                            </div>
                          ) : null;
                        })()}
                      </>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: 'var(--color-info-light)', border: '1px solid var(--color-info)', borderRadius: 6, fontSize: 13 }}>
                        <span style={{ fontWeight: 700, color: 'var(--color-primary)' }}>{form.invoiceNumber}</span>
                        <span style={{ color: 'var(--color-text-secondary)' }}>{invoices.find(i => String(i.id) === String(form.linkedInvoiceId))?.customerName || ''}</span>
                        <span style={{ color: 'var(--color-primary)', fontWeight: 600, marginLeft: 'auto' }}>{form.exportCurrency} {Number(form.invoiceValue || 0).toLocaleString()}</span>
                        <button type="button" onClick={() => { handleInvoiceSelect(''); setInvoiceSearch(''); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', fontWeight: 700, fontSize: 16, lineHeight: 1, padding: '0 2px' }}>×</button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="gd-field">
                  <label>Invoice Value * ({form.exportCurrency})</label>
                  <input type="number" className="gd-input gd-full" placeholder="FOB/CIF value"
                    min="0.01" step="0.01" value={form.invoiceValue} onChange={f('invoiceValue')} required />
                </div>
                <div className="gd-field gd-span-2">
                  <label>Shipment Description</label>
                  <textarea className="gd-textarea gd-full" rows={2} placeholder="What goods are being exported?"
                    value={form.shipmentDescription} onChange={f('shipmentDescription')} />
                </div>
              </div>

              <div className="gd-section-label" style={{ marginTop: 20 }}>Bank & Settlement</div>
              <div className="gd-grid-2">
                <div className="gd-field">
                  <label>Bank Name</label>
                  <input className="gd-input gd-full" placeholder="e.g. HBL, UBL"
                    value={form.bankName} onChange={f('bankName')} />
                </div>
                <div className="gd-field">
                  <label>Bank Account</label>
                  <input className="gd-input gd-full" value={form.bankAccountNumber} onChange={f('bankAccountNumber')} />
                </div>
                <div className="gd-field">
                  <label>Docs Submitted to Bank</label>
                  <input type="date" className="gd-input gd-full" value={form.documentsSubmittedDate} onChange={f('documentsSubmittedDate')} />
                </div>
                <div className="gd-field">
                  <label>Settlement Window (days)</label>
                  <select className="gd-select gd-full" value={form.settlementWindowDays} onChange={f('settlementWindowDays')}>
                    {[15, 30, 45, 60, 90, 120].map(d => <option key={d} value={d}>{d} days</option>)}
                  </select>
                </div>
                <div className="gd-field">
                  <label>Exchange Rate (USD→PKR)</label>
                  <input type="number" className="gd-input gd-full" placeholder="e.g. 278.50"
                    min="0" step="0.0001" value={form.exchangeRate} onChange={f('exchangeRate')} />
                </div>
                <div className="gd-field">
                  <label>Bank Charges (PKR)</label>
                  <input type="number" className="gd-input gd-full" placeholder="0.00"
                    min="0" step="0.01" value={form.bankCharges} onChange={f('bankCharges')} />
                </div>
                <div className="gd-field">
                  <label>Net PKR Received</label>
                  <input type="number" className="gd-input gd-full" placeholder="0.00"
                    min="0" step="0.01" value={form.netPkrReceived} onChange={f('netPkrReceived')} />
                </div>
                <div className="gd-field">
                  <label>Settlement Date</label>
                  <input type="date" className="gd-input gd-full" value={form.settlementDate} onChange={f('settlementDate')} />
                </div>
                <div className="gd-field">
                  <label>Status</label>
                  <select className="gd-select gd-full" value={form.status} onChange={f('status')}>
                    {GD_STATUSES.map(s => <option key={s} value={s}>{formatEnum(s)}</option>)}
                  </select>
                </div>
                <div className="gd-field gd-span-2">
                  <label>Notes</label>
                  <textarea className="gd-textarea gd-full" rows={2} value={form.notes} onChange={f('notes')} />
                </div>
              </div>

              <div className="gd-section-label" style={{ marginTop: 20 }}>Link to Existing Records</div>
              <div className="gd-grid-2">
                <div className="gd-field">
                  <label>Linked Gate Pass (Exit)</label>
                  <SearchableSelect
                    placeholder="— None —"
                    value={form.linkedGatePassId}
                    options={gatePasses.map(gp => ({
                      value: gp.id,
                      label: `${gp.gatePassNumber} — ${gp.type} (${gp.partyName})`,
                    }))}
                    onChange={(value) => f('linkedGatePassId')({ target: { value: String(value) } })}
                  />
                </div>
              </div>

              <div className="gd-modal-footer">
                <button type="button" className="gd-btn gd-btn-outline" onClick={() => { setShowFormModal(false); setEditingGD(null); }}>Cancel</button>
                <button type="submit" className="gd-btn gd-btn-primary" disabled={formLoading}>
                  {formLoading ? 'Saving...' : editingGD ? 'Update GD' : 'Create GD'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== DETAIL MODAL ===== */}
      {showDetailModal && detailGD && (
        <div className="gd-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="gd-modal gd-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="gd-modal-header">
              <div>
                <h2>{detailGD.gdNumber}</h2>
                <StatusBadge value={detailGD.status} />
              </div>
              <button className="gd-modal-close" onClick={() => setShowDetailModal(false)}>×</button>
            </div>
            <div className="gd-modal-body">
              <div className="gd-detail-grid">
                <Row label="Consignee" value={`${detailGD.consigneeName}${detailGD.consigneeCountry ? ', ' + detailGD.consigneeCountry : ''}`} />
                <Row label="Invoice Value" value={formatUSD(detailGD.invoiceValue)} />
                <Row label="Filing Date" value={formatDate(detailGD.filingDate)} />
                <Row label="Bill of Lading" value={detailGD.billOfLadingNumber} />
                <Row label="Invoice #" value={detailGD.invoiceNumber} />
                <Row label="Port of Loading" value={detailGD.portOfLoading} />
                <Row label="Destination" value={detailGD.portOfDestination} />
                <Row label="Vessel / Flight" value={detailGD.vesselOrFlight} />
                <Row label="Bank" value={detailGD.bankName} />
                <Row label="Docs Submitted" value={formatDate(detailGD.documentsSubmittedDate)} />
                <Row label="Settlement Window" value={detailGD.settlementWindowDays ? `${detailGD.settlementWindowDays} days` : '—'} />
                <Row label="Settlement Deadline" value={formatDate(detailGD.settlementDeadline)} />
                <Row label="Exchange Rate" value={detailGD.exchangeRate ? `1 USD = PKR ${detailGD.exchangeRate}` : '—'} />
                <Row label="Bank Charges" value={formatPKR(detailGD.bankCharges)} />
                <Row label="Net PKR Received" value={formatPKR(detailGD.netPkrReceived)} />
                <Row label="Settlement Date" value={formatDate(detailGD.settlementDate)} />
                <Row label="Linked Invoice" value={detailGD.linkedInvoiceNumber ? `#${detailGD.linkedInvoiceNumber}` : '—'} />
                <Row label="Linked Gate Pass" value={detailGD.linkedGatePassNumber || '—'} />
                {detailGD.shipmentDescription && <Row label="Description" value={detailGD.shipmentDescription} span />}
                {detailGD.notes && <Row label="Notes" value={detailGD.notes} span />}
              </div>

              {/* Documents */}
              <div className="gd-docs-section">
                <div className="gd-docs-header">
                  <span>Documents ({detailGD.documents?.length || 0})</span>
                  <button className="gd-btn gd-btn-sm gd-btn-outline" onClick={() => { setDocForm(emptyDocForm()); setShowDocModal(true); }}>
                    + Add Document
                  </button>
                </div>
                {detailGD.documents?.length === 0 && (
                  <div className="gd-docs-empty">No documents attached yet.</div>
                )}
                {detailGD.documents?.map(doc => (
                  <div key={doc.id} className="gd-doc-row">
                    <div className="gd-doc-left">
                      <span className="gd-doc-type">{formatEnum(doc.documentType)}</span>
                      <span className="gd-doc-name">{doc.documentName}</span>
                      {doc.referenceNumber && <span className="gd-doc-ref">Ref: {doc.referenceNumber}</span>}
                      {doc.documentDate && <span className="gd-doc-date">{formatDate(doc.documentDate)}</span>}
                    </div>
                    <button className="gd-btn-link gd-link-danger" onClick={() => setRemoveDocId(doc.id)}>Remove</button>
                  </div>
                ))}
              </div>
            </div>
            {/* File Attachments */}
            <div style={{ marginTop: 16, padding: '0 20px 16px' }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-secondary)' }}>File Attachments</h4>
              <AttachmentPanel entityType="GD" entityId={detailGD.id} />
            </div>
            <div className="gd-modal-footer">
              <button className="gd-btn gd-btn-outline" onClick={() => setShowDetailModal(false)}>Close</button>
              <button className="gd-btn gd-btn-primary" onClick={() => { setShowDetailModal(false); openEdit(detailGD); }}>Edit GD</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== ADD DOCUMENT MODAL ===== */}
      {showDocModal && (
        <div className="gd-overlay" style={{ zIndex: 1100 }} onClick={() => setShowDocModal(false)}>
          <div className="gd-modal gd-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="gd-modal-header">
              <h2>Add Document</h2>
              <button className="gd-modal-close" onClick={() => setShowDocModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddDocument} className="gd-modal-body">
              <div className="gd-grid-2">
                <div className="gd-field">
                  <label>Document Type *</label>
                  <select className="gd-select gd-full" value={docForm.documentType}
                    onChange={e => setDocForm({ ...docForm, documentType: e.target.value })}>
                    {DOC_TYPES.map(t => <option key={t} value={t}>{formatEnum(t)}</option>)}
                  </select>
                </div>
                <div className="gd-field">
                  <label>Document Name *</label>
                  <input className="gd-input gd-full" placeholder="e.g. BL-2026-123"
                    value={docForm.documentName}
                    onChange={e => setDocForm({ ...docForm, documentName: e.target.value })} required />
                </div>
                <div className="gd-field">
                  <label>Document Date</label>
                  <input type="date" className="gd-input gd-full" value={docForm.documentDate}
                    onChange={e => setDocForm({ ...docForm, documentDate: e.target.value })} />
                </div>
                <div className="gd-field">
                  <label>Reference Number</label>
                  <input className="gd-input gd-full" value={docForm.referenceNumber}
                    onChange={e => setDocForm({ ...docForm, referenceNumber: e.target.value })} />
                </div>
                <div className="gd-field gd-span-2">
                  <label>Description</label>
                  <textarea className="gd-textarea gd-full" rows={2} value={docForm.description}
                    onChange={e => setDocForm({ ...docForm, description: e.target.value })} />
                </div>
              </div>
              <div className="gd-modal-footer">
                <button type="button" className="gd-btn gd-btn-outline" onClick={() => setShowDocModal(false)}>Cancel</button>
                <button type="submit" className="gd-btn gd-btn-primary" disabled={docLoading}>
                  {docLoading ? 'Adding...' : 'Add Document'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== STATUS MODAL ===== */}
      {showStatusModal && statusGD && (
        <div className="gd-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="gd-modal gd-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="gd-modal-header">
              <h2>Update Status</h2>
              <button className="gd-modal-close" onClick={() => setShowStatusModal(false)}>×</button>
            </div>
            <div className="gd-modal-body">
              <p className="gd-status-info">{statusGD.gdNumber} — {statusGD.consigneeName}</p>
              <div className="gd-field">
                <label>New Status</label>
                <select className="gd-select gd-full" value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                  {GD_STATUSES.map(s => <option key={s} value={s}>{formatEnum(s)}</option>)}
                </select>
              </div>
            </div>
            <div className="gd-modal-footer">
              <button className="gd-btn gd-btn-outline" onClick={() => setShowStatusModal(false)}>Cancel</button>
              <button className="gd-btn gd-btn-primary" onClick={handleUpdateStatus} disabled={statusLoading}>
                {statusLoading ? 'Saving...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== DELETE MODAL ===== */}
      {showDeleteModal && deleteGD && (
        <div className="gd-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="gd-modal gd-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="gd-modal-header">
              <h2>Confirm Delete</h2>
              <button className="gd-modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="gd-modal-body">
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.6, margin: 0 }}>
                Delete GD <strong>{deleteGD.gdNumber}</strong>? This will also remove all attached documents. <strong>Cannot be undone.</strong>
              </p>
            </div>
            <div className="gd-modal-footer">
              <button className="gd-btn gd-btn-outline" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className="gd-btn gd-btn-danger" onClick={handleDelete} disabled={deleteLoading}>
                {deleteLoading ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Document Confirmation */}
      {removeDocId && (
        <div className="modal-overlay" onClick={() => setRemoveDocId(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h3>Remove Document</h3>
              <button className="modal-close" onClick={() => setRemoveDocId(null)}>&times;</button>
            </div>
            <p style={{ padding: '0 20px', color: 'var(--color-text-secondary)' }}>Are you sure you want to remove this document from this GD?</p>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setRemoveDocId(null)}>Cancel</button>
              <button className="btn-danger" onClick={handleRemoveDocument}>Remove</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          background: toast.type === 'error' ? 'var(--color-error-light)' : 'var(--color-success-light)',
          color: toast.type === 'error' ? 'var(--color-error)' : 'var(--color-success)',
          border: `1px solid ${toast.type === 'error' ? 'var(--color-error)' : 'var(--color-success)'}`,
          padding: '14px 22px', borderRadius: 10, fontSize: 14, fontWeight: 600,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, span }) {
  return (
    <div className={`gd-detail-row${span ? ' gd-detail-span' : ''}`}>
      <span className="gd-detail-label">{label}</span>
      <span className="gd-detail-value">{value || '—'}</span>
    </div>
  );
}
